import { onBeforeUnmount, onMounted } from 'vue';

import { serviceWorkerManager } from '../services/sw.manager';
import { addBroadcastMessageListener } from './broadcast.helpers';

export class IdleTimer {
    private static lastInteractionMs = Date.now();
    private static hasUserInteraction = false;
    private static notifySwOnNextInteraction = false;
    private static notifyServerOnNextInteraction = false;
    private static serverNotIdleHandler: (() => void) | undefined;
    private static browserSessionScopeProvider: (() => string | null) | undefined;
    private static lastSharedWriteMs = 0;
    private static sharedWriteTimeout: number | undefined;

    static registerBrowserSessionScopeProvider(provider: () => string | null) {
        this.browserSessionScopeProvider = provider;
        const scope = provider();
        if (scope) {
            const sharedTs = this.readSharedInteraction(scope);
            // Loading/reloading an idle tab is not a new interaction with the session.
            if (sharedTs) this.lastInteractionMs = Math.max(sharedTs, this.hasUserInteraction ? this.lastInteractionMs : 0);
            else this.writeSharedInteraction();
        }
    }

    private static readSharedInteraction(scope: string) {
        try {
            const ts = Number(localStorage.getItem(`zs:auth:lastActivity:${scope}`));
            return Number.isFinite(ts) && ts > 0 && ts <= Date.now() ? ts : 0;
        } catch {
            return 0;
        }
    }

    private static writeSharedInteraction() {
        this.sharedWriteTimeout = undefined;
        const scope = this.browserSessionScopeProvider?.();
        if (!scope) return;
        try {
            const latest = Math.max(this.lastInteractionMs, this.readSharedInteraction(scope));
            localStorage.setItem(`zs:auth:lastActivity:${scope}`, String(latest));
            this.lastSharedWriteMs = Date.now();
        } catch {
            // Storage unavailable: retain the local idle clock.
        }
    }

    /** A mounted timeout disabler (e.g. an ongoing call) also keeps sibling tabs alive. */
    static keepSessionActive() {
        this.lastInteractionMs = Date.now();
        this.hasUserInteraction = true;
        this.writeSharedInteraction();
    }

    private static updateUserInteractionTs = () => {
        this.lastInteractionMs = Date.now();
        this.hasUserInteraction = true;
        // Persist at most once a second during mouse movement, including the trailing event.
        if (Date.now() - this.lastSharedWriteMs >= 1_000) this.writeSharedInteraction();
        else if (this.sharedWriteTimeout === undefined) {
            this.sharedWriteTimeout = window.setTimeout(() => this.writeSharedInteraction(), 1_000);
        }
        if (this.notifySwOnNextInteraction) {
            serviceWorkerManager.postMessage('userActivity', undefined);
            this.notifySwOnNextInteraction = false;
        }
        if (this.notifyServerOnNextInteraction) {
            this.notifyServerNotIdle();
        }
    };

    static setup() {
        // Capture includes non-bubbling panel scrolls and events stopped by child controls.
        window.addEventListener('mousemove', this.updateUserInteractionTs, true);
        window.addEventListener('keydown', this.updateUserInteractionTs, true);
        window.addEventListener('scroll', this.updateUserInteractionTs, true);
        window.addEventListener('click', this.updateUserInteractionTs, true);
        window.addEventListener('pointerdown', this.updateUserInteractionTs, true);
        window.addEventListener('touchstart', this.updateUserInteractionTs, true);

        addBroadcastMessageListener('setNotifySwOnNextInteraction', () => {
            this.notifySwOnNextInteraction = true;
        });

        serviceWorkerManager.onMessage('notifyServerNotIdle', () => {
            this.notifyServerNotIdle();
        });
    }

    static teardown() {
        window.removeEventListener('mousemove', this.updateUserInteractionTs, true);
        window.removeEventListener('keydown', this.updateUserInteractionTs, true);
        window.removeEventListener('scroll', this.updateUserInteractionTs, true);
        window.removeEventListener('click', this.updateUserInteractionTs, true);
        window.removeEventListener('pointerdown', this.updateUserInteractionTs, true);
        window.removeEventListener('touchstart', this.updateUserInteractionTs, true);
        window.clearTimeout(this.sharedWriteTimeout);
        this.sharedWriteTimeout = undefined;
    }

    /**
     * Register what to do when the user resumes activity after being idle — the full app pings the
     * server (via the auth client) so it knows the session is live again. Kept as a registration seam
     * so this low-level helper carries no dependency on the auth client: lean bundles (e.g. the
     * embedded CX app) that reach IdleTimer never pull the client in and simply leave it
     * unregistered, making notifyServerNotIdle a no-op there. Installed by the full app in main.ts.
     */
    static registerServerNotIdleHandler(handler: () => void) {
        this.serverNotIdleHandler = handler;
    }

    static notifyServerNotIdle() {
        this.serverNotIdleHandler?.();
    }

    static setNotifyServerNotIdleOnResume() {
        this.notifyServerOnNextInteraction = true;
    }

    static getIdleMs() {
        const scope = this.browserSessionScopeProvider?.();
        const sharedTs = scope ? this.readSharedInteraction(scope) : 0;
        // Never let a delayed write make a locally active tab appear idle.
        return Date.now() - Math.max(this.lastInteractionMs, sharedTs);
    }
}

export function useIdleTimeout(options: { checkIntervalMs?: number; timeoutMs: number; onIdle: (idleMs: number) => void }) {
    let checkInteractionInterval: number | undefined;

    const checkLastInteractionTs = () => {
        const idleMs = IdleTimer.getIdleMs();
        if (idleMs >= options.timeoutMs) {
            options.onIdle(idleMs);
        }
    };

    onMounted(() => {
        checkInteractionInterval = window.setInterval(checkLastInteractionTs, options.checkIntervalMs ?? 10_000);
    });

    onBeforeUnmount(() => {
        if (checkInteractionInterval) clearInterval(checkInteractionInterval);
    });
}

IdleTimer.setup();
